import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Row, Button } from 'antd'
import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons'
import * as d3 from 'd3'
import moment from 'moment'
import '../../../css/patient.scss'
// No ShowValuesSwitch here: this chart has never printed values on its bars --
// a stacked bar has six bands and the numbers live in the tooltip -- so there
// is nothing for the switch to reveal or hide.
import { styleAxisTicks, AXIS_TITLE_FONT_SIZE, AXIS_TITLE_FONT_WEIGHT } from './tracpatch.chart.style'
import { earliestDay, isTodayOrLater, compactHourLabel } from './tracpatch.hourly'
import { DataViewToggle, ChartDataGrid } from '../../graph/chartDataView'

// Daily Leg Position Distribution
//
// Time accounting (Option A — record-based, honest):
//   For each passive ROM record on a day, accumulate its window duration
//   (60s) into the band that matches its peak flexion.
//   For each step record where steps > 0, accumulate its duration (60s) into
//   Active. Walking wins any minute it shares with a ROM record — see below.
//
// ⚠️ THE TWO SIGNALS DO OVERLAP. An earlier version of this file asserted the
// opposite ("passive ROM events do not fire while walking, 0% overlap of 64
// real-device records") and stacked both without deduplication. That claim was
// measured on a small local sample and is FALSE on real data: on prod2,
// 2026-08-14, 16 of 24 walking minutes also carried a passive ROM record — 67%.
//
// The consequence was double-counting: a shared minute contributed 60s to a
// flexion band AND 60s to Active, so the chart reported 120 seconds of leg
// position for 60 seconds of elapsed time. The error scaled with how much the
// patient walked, and in the 15-minute view produced buckets claiming more than
// 15 minutes of observed time — impossible on its face. It was invisible in the
// default `%` unit, which normalises each bucket to 100% of *observed* time.
//
// Fix: build the set of minutes containing step activity FIRST, then skip any
// ROM record overlapping one. Walking is the more specific signal — a minute
// spent walking is not time the leg was held at a position — so it takes the
// minute. This enforces the disjointness the stacking model always assumed.
//
// Note the two record types are phase-offset (ROM windows start at :19/:34/:54,
// step windows at :51), so overlap is tested across every minute each window
// touches rather than by comparing start minutes.

const BANDS = [
  { key: 'lt15',     label: '< 15°',     color: '#FFD93D', min: -Infinity, max: 15  }, // yellow
  { key: 'b15_45',   label: '15–45°',    color: '#8B5A2B', min: 15,  max: 45  },       // brown
  { key: 'b45_75',   label: '45–75°',    color: '#2E8B3D', min: 45,  max: 75  },       // green
  { key: 'b75_115',  label: '75–115°',   color: '#ADFF2F', min: 75,  max: 115 },       // neon/light green
  { key: 'b115_130', label: '115–130°',  color: '#E53935', min: 115, max: 130 },       // red
  { key: 'active',   label: 'Active',    color: '#1976D2', min: null, max: null },     // blue, walking from steps
]

// Working-range gate to drop anomalous records (matches the ROM-test bounds
// and the admin ROM chart's fixed y-axis).
const VALID_FLEX_MIN = -15
const VALID_FLEX_MAX = 130

const bucketByFlexion = (flex) => {
  if (flex == null || flex < VALID_FLEX_MIN || flex > VALID_FLEX_MAX) return null
  for (let i = 0; i < BANDS.length - 1; i++) {
    const b = BANDS[i]
    if (flex >= b.min && flex < b.max) return b.key
  }
  return null
}

const MODES = {
  // 1 Day is the 15-minute view. It was "Today (15m)" and hard-wired to today;
  // it now renders whichever day `day` holds, so it can be navigated with the
  // arrows and reached by clicking a bar in a multi-day view -- the same shape
  // the Steps and Temperature charts use for their 1-Day view.
  D1:  { id: 'D1',  label: '1 Day',   bars: 96, intervalMs: 15 * 60 * 1000,   daily: false },
  D7:  { id: 'D7',  label: '7 Days',  bars: 7,  intervalMs: 24 * 3600 * 1000, daily: true },
  D14: { id: 'D14', label: '14 Days', bars: 14, intervalMs: 24 * 3600 * 1000, daily: true },
  D30: { id: 'D30', label: '30 Days', bars: 30, intervalMs: 24 * 3600 * 1000, daily: true },
}

const TracPatchLegPositionChart = (props) => {
  const svgRef = useRef(null)
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  const [modeId, setModeId] = useState('D30')
  const [unit, setUnit] = useState('pct') // 'pct' = % of observed time (default), 'time' = hours/minutes
  const [view, setView] = useState('chart')
  // The day the 15-minute view renders. Today until a bar is clicked.
  const [day, setDay] = useState(() => moment().startOf('day'))

  useEffect(() => {
    if (view === 'chart') draw()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.romData, props.stepsData, modeId, unit, view, day.valueOf()])

  const buildBuckets = () => {
    const mode = MODES[modeId]
    let windowStart, windowEnd
    if (mode.daily) {
      windowEnd = moment().endOf('day')
      windowStart = moment().subtract(mode.bars - 1, 'days').startOf('day')
    } else {
      windowStart = moment(day).startOf('day')
      windowEnd = moment(day).endOf('day')
    }

    // Pre-allocate one bucket per bar with zeros for every band.
    const buckets = []
    let cursor = windowStart.clone()
    for (let i = 0; i < mode.bars; i++) {
      const start = cursor.clone()
      const end = cursor.clone().add(mode.intervalMs, 'ms')
      const seconds = {}
      BANDS.forEach((b) => { seconds[b.key] = 0 })
      buckets.push({ index: i, start: start.toDate(), end: end.toDate(), seconds })
      cursor = end.clone()
    }

    const indexFor = (t) => {
      const ms = t.getTime() - windowStart.toDate().getTime()
      if (ms < 0 || ms >= mode.bars * mode.intervalMs) return -1
      return Math.floor(ms / mode.intervalMs)
    }

    // 0) Every minute that contains step activity. Built BEFORE the ROM pass so
    // a shared minute can be attributed to Active alone (see the header note).
    // A window is recorded against every minute it touches, because ROM and
    // step windows are phase-offset and a naive start-minute comparison would
    // miss most real overlaps.
    const MIN_MS = 60 * 1000
    const minutesOf = (startMs, endMs) => {
      const first = Math.floor(startMs / MIN_MS)
      const last = Math.floor(Math.max(startMs, endMs - 1) / MIN_MS)
      const out = []
      for (let m = first; m <= last; m++) out.push(m)
      return out
    }
    const walkingMinutes = new Set()
    if (Array.isArray(props.stepsData)) {
      props.stepsData.forEach((rec) => {
        if (!rec || !rec.startTime || !rec.endTime) return
        if (!(rec.steps > 0)) return
        const s = new Date(rec.startTime).getTime()
        const e = new Date(rec.endTime).getTime()
        if ((e - s) / 1000 > 5 * 60) return // demo seed records, see pass 2
        minutesOf(s, e).forEach((m) => walkingMinutes.add(m))
      })
    }

    // 1) Passive ROM records → static bands
    if (Array.isArray(props.romData)) {
      props.romData.forEach((rec) => {
        if (!rec || rec.isForceSync) return
        const start = rec.startTime ? new Date(rec.startTime) : null
        const end = rec.endTime ? new Date(rec.endTime) : null
        if (!start || !end) return
        // Walking wins the minute. Without this the same 60 seconds is counted
        // into both a flexion band and Active.
        if (minutesOf(start.getTime(), end.getTime()).some((m) => walkingMinutes.has(m))) return
        const idx = indexFor(start)
        if (idx < 0) return
        const bandKey = bucketByFlexion(rec.flexion)
        if (!bandKey) return
        const dur = Math.max(0, (end.getTime() - start.getTime()) / 1000)
        buckets[idx].seconds[bandKey] += dur
      })
    }

    // 2) Step records with steps > 0 → Active
    if (Array.isArray(props.stepsData)) {
      props.stepsData.forEach((rec) => {
        if (!rec || !rec.startTime || !rec.endTime) return
        if (!(rec.steps > 0)) return
        // Real device records are 60-second windows. Demo seed records can be
        // 24-hour-wide and would distort the chart — skip those.
        const dur = (new Date(rec.endTime).getTime() - new Date(rec.startTime).getTime()) / 1000
        if (dur > 5 * 60) return // anything longer than 5 min isn't a real minute-window record
        const idx = indexFor(new Date(rec.startTime))
        if (idx < 0) return
        buckets[idx].seconds.active += dur
      })
    }

    // Daily modes: trim empty bars from BOTH ends so the chart spans the days
    // that actually have data (matches Steps / Cadence / Temperature). The
    // 15-min view keeps its full-day frame -- a day's shape is the point there,
    // and trimming it would move midnight off the left edge.
    //
    // Leading empties were never trimmed, so a patient registered four days ago
    // had their bars pinned to the right-hand edge of a 30-day frame with a
    // month of blank chart in front of them. Every other chart left-justifies.
    if (mode.daily) {
      let lastIdx = buckets.length - 1
      while (lastIdx > 0) {
        const total = BANDS.reduce(
          (sum, b) => sum + (buckets[lastIdx].seconds[b.key] || 0),
          0
        )
        if (total > 0) break
        lastIdx--
      }
      if (lastIdx < buckets.length - 1) {
        buckets.length = lastIdx + 1
        windowEnd = moment(buckets[lastIdx].end)
      }

      const isEmpty = (b) => BANDS.reduce((sum, band) => sum + (b.seconds[band.key] || 0), 0) === 0
      let firstIdx = 0
      while (firstIdx < buckets.length - 1 && isEmpty(buckets[firstIdx])) firstIdx++
      if (firstIdx > 0) {
        buckets.splice(0, firstIdx)
        // The bars are positioned by `index`, so renumber after the splice or
        // every bar draws one slot to the right of where it belongs.
        buckets.forEach((b, i) => { b.index = i })
        windowStart = moment(buckets[0].start)
      }
    }

    return { buckets, mode, windowStart: windowStart.toDate(), windowEnd: windowEnd.toDate() }
  }

  const draw = () => {
    const { buckets, mode, windowStart, windowEnd } = buildBuckets()
    if (!buckets || buckets.length === 0) return

    const margin = { top: 30, right: 20, bottom: 70, left: 60 }
    const screenWidth = containerRef.current ? containerRef.current.offsetWidth : 800
    const screenHeight = 360
    const width = screenWidth - margin.left - margin.right
    const height = screenHeight - margin.top - margin.bottom

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    // A redraw replaces the point under the pointer, so its mouseout never
    // fires and a tooltip left visible would float there until the next
    // hover. Every draw therefore starts with the tooltip hidden.
    d3.select(tooltipRef.current).style('visibility', 'hidden')

    const g = svg
      .attr('width', screenWidth)
      .attr('height', screenHeight)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    const x = d3.scaleBand()
      .domain(buckets.map((b) => b.index))
      .range([0, width])
      .padding(0.15)

    // Y axis units: hours for daily modes, minutes for 15-min mode, or % when
    // toggle is set to 'pct' (each bar normalized to 100% of its observed time).
    const isDaily = mode.daily
    const isPct = unit === 'pct'
    const unitDivisor = isPct ? 1 : (isDaily ? 3600 : 60)
    const unitLabel = isPct ? '%' : (isDaily ? 'Hours' : 'Minutes')
    const yMax = isPct ? 100 : (isDaily ? 24 : 15)

    const y = d3.scaleLinear().domain([0, yMax]).range([height, 0])

    // Pre-compute per-bar total observed seconds so we can normalize to %.
    const totalSecondsPerBar = buckets.map((b) =>
      BANDS.reduce((sum, band) => sum + (b.seconds[band.key] || 0), 0)
    )

    // X axis labels — date for daily; for 15-min buckets the hour as the Steps
    // chart writes it ("12a", "2a" … "6p"), not 24-hour "18:00".
    const xTickFormat = (i) => {
      const m = moment(buckets[i].start)
      if (isDaily) return m.format('MM/DD')
      return compactHourLabel(m.hour())
    }
    const xTickEvery = isDaily
      ? (mode.bars > 20 ? 3 : 2)
      : 8 // every 8 * 15min = every 2 hours

    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x)
        .tickFormat((i) => (i % xTickEvery === 0 ? xTickFormat(i) : ''))
      )
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.6em')
      .attr('dy', '.25em')
      .attr('transform', 'rotate(-45)')
      .call(styleAxisTicks, 9)
      .style('fill', 'black')
      .style('stroke', 'none')

    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('x', width / 2)
      .attr('y', height + margin.bottom - 8)
      .attr('fill', 'black')
      .attr('stroke', 'none')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text(isDaily ? 'Date' : 'Time of day')

    g.append('g')
      .call(d3.axisLeft(y).ticks(6))
      .selectAll('text')
      .style('fill', 'black')
      .style('stroke', 'none')
      .call(styleAxisTicks, 10)

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 14)
      .attr('x', -height / 2)
      .attr('text-anchor', 'middle')
      .attr('fill', 'black')
      .attr('stroke', 'none')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text('Position ' + unitLabel)

    // Surgery-date marker (daily modes only).
    if (isDaily) {
      const surgeryRaw = props.surgeryDate
      if (surgeryRaw) {
        const s = moment(surgeryRaw).startOf('day').toDate()
        if (s >= windowStart && s <= windowEnd) {
          // Find the bar for the surgery day.
          const idx = buckets.findIndex((b) =>
            moment(b.start).isSame(moment(s), 'day')
          )
          if (idx >= 0) {
            const sx = x(idx) + x.bandwidth() / 2
            g.append('line')
              .attr('x1', sx)
              .attr('x2', sx)
              .attr('y1', 0)
              .attr('y2', height)
              .attr('stroke', '#000')
              .attr('stroke-width', 1.5)
              .attr('stroke-dasharray', '4,3')
            const labelText = 'Surgery ' + moment(s).format('M/D')
            const labelOnLeft = sx > width - 80
            g.append('text')
              .attr('x', sx + (labelOnLeft ? -4 : 4))
              .attr('y', -4)
              .attr('text-anchor', labelOnLeft ? 'end' : 'start')
              .style('font-size', '10px')
              .style('font-weight', '600')
              .style('fill', '#000')
              .text(labelText)
          }
        }
      }
    }

    const tooltip = d3.select(tooltipRef.current)

    // Stacked bars. In % mode each bar is normalized to 100% of its own
    // observed time, so an empty bar stays empty (no bogus 100% fill from
    // dividing 0/0).
    const valueFor = (sec, totalSec) => {
      if (isPct) return totalSec > 0 ? (sec / totalSec) * 100 : 0
      return sec / unitDivisor
    }
    const unitSuffix = isPct ? '%' : (isDaily ? 'h' : 'min')

    buckets.forEach((b, bi) => {
      const totalSec = totalSecondsPerBar[bi]
      let yOffset = 0
      BANDS.forEach((band) => {
        const sec = b.seconds[band.key] || 0
        if (sec <= 0) return
        const value = valueFor(sec, totalSec)
        const yTop = y(yOffset + value)
        const yBot = y(yOffset)
        const rect = g.append('rect')
          .attr('x', x(b.index))
          .attr('y', yTop)
          .attr('width', x.bandwidth())
          .attr('height', Math.max(0, yBot - yTop))
          .attr('fill', band.color)
          .style('cursor', isDaily ? 'zoom-in' : 'pointer')
        if (isDaily) {
          rect.on('click', () => {
            tooltip.style('visibility', 'hidden')
            setDay(moment(b.start).startOf('day'))
            setModeId('D1')
          })
        }
        rect.on('mouseover', function () {
          const dateLabel = moment(b.start).format(isDaily ? 'MM/DD' : 'MM/DD h:mm A')
          const lines = BANDS
            .map((bb) => {
              const s = b.seconds[bb.key] || 0
              if (s <= 0) return null
              const v = valueFor(s, totalSec).toFixed(1)
              return `<span style="color:${bb.color}">■</span> <b>${bb.label}</b>: ${v} ${unitSuffix}`
            })
            .filter(Boolean)
            .join('<br/>')
          tooltip
            .style('visibility', 'visible')
            .html(`<b>${dateLabel}</b><br/>${lines}`)
            .style('top', yTop - 10 + 'px')
            .style('left', x(b.index) + x.bandwidth() + 10 + margin.left + 'px')
        })
        rect.on('mouseout', () => tooltip.style('visibility', 'hidden'))
        yOffset += value
      })
    })

    // Legend
    const legendY = -22
    let xOffset = 0
    BANDS.forEach((band) => {
      g.append('rect')
        .attr('x', xOffset)
        .attr('y', legendY)
        .attr('width', 10)
        .attr('height', 10)
        .attr('fill', band.color)
      g.append('text')
        .attr('x', xOffset + 14)
        .attr('y', legendY + 9)
        .attr('fill', 'black')
        .attr('stroke', 'none')
        .style('font-size', '10px')
        .text(band.label)
      xOffset += band.label.length * 6 + 28
    })
  }

  const { buckets, mode } = buildBuckets()
  const hasAnyData = buckets.some((b) =>
    Object.values(b.seconds).some((v) => v > 0)
  )

  // Data-view grid: one row per bucket that has any observed time, one column
  // per band. Values mirror the chart's current unit (% of observed time, or
  // hours/minutes). Empty bands render as an em-dash so the grid stays sparse.
  const isPct = unit === 'pct'
  // Back-arrow bound: the earliest record in EITHER feed. A day can carry ROM
  // with no steps, or steps with no ROM, so taking one feed alone would stop
  // the navigation short of real data.
  const firstRom = earliestDay(props.romData, 'startTime')
  const firstSteps = earliestDay(props.stepsData, 'startTime')
  const firstDay =
    firstRom && firstSteps ? (firstRom.isBefore(firstSteps) ? firstRom : firstSteps) : firstRom || firstSteps
  const isDaily = mode.daily
  const unitDivisor = isPct ? 1 : (isDaily ? 3600 : 60)
  const unitSuffix = isPct ? '%' : (isDaily ? 'h' : 'min')
  const gridColumns = BANDS.map((b) => ({ title: b.label, dataIndex: b.key, key: b.key }))
  const gridRows = buckets
    .filter((b) => BANDS.some((band) => (b.seconds[band.key] || 0) > 0))
    .map((b) => {
      const totalSec = BANDS.reduce((s, band) => s + (b.seconds[band.key] || 0), 0)
      const row = { date: b.start }
      BANDS.forEach((band) => {
        const sec = b.seconds[band.key] || 0
        if (sec <= 0) {
          row[band.key] = '—'
          return
        }
        const v = isPct ? (totalSec > 0 ? (sec / totalSec) * 100 : 0) : sec / unitDivisor
        row[band.key] = v.toFixed(1) + (isPct ? '%' : ' ' + unitSuffix)
      })
      return row
    })

  return (
    <>
      <center>
        <h4>TracPatch Daily Leg Position</h4>
      </center>
      <Row justify='start' className='chartControls' style={{ marginBottom: 8, paddingLeft: 12 }}>
        <Button.Group size='small'>
          <Button type={modeId === 'D1' ? 'primary' : 'default'} onClick={() => { setDay(moment().startOf('day')); setModeId('D1') }}>1 Day</Button>
          <Button type={modeId === 'D7' ? 'primary' : 'default'} onClick={() => setModeId('D7')}>7 Days</Button>
          <Button type={modeId === 'D14' ? 'primary' : 'default'} onClick={() => setModeId('D14')}>14 Days</Button>
          <Button type={modeId === 'D30' ? 'primary' : 'default'} onClick={() => setModeId('D30')}>30 Days</Button>
        </Button.Group>
        <Button.Group size='small'>
          <Button type={unit === 'time' ? 'primary' : 'default'} onClick={() => setUnit('time')}>Time</Button>
          <Button type={unit === 'pct'  ? 'primary' : 'default'} onClick={() => setUnit('pct')}>%</Button>
        </Button.Group>
        <DataViewToggle
          active={view === 'data'}
          onClick={() => setView(view === 'data' ? 'chart' : 'data')}
        />
      </Row>
      {!mode.daily && (
        <Row justify='center' align='middle' className='chartDayNav' style={{ marginBottom: 8, gap: 8 }}>
          <Button
            size='small'
            aria-label='Previous day'
            icon={<CaretLeftOutlined />}
            disabled={!firstDay || !day.isAfter(firstDay)}
            onClick={() => setDay(day.clone().subtract(1, 'day').startOf('day'))}
          />
          <span style={{ minWidth: 150, textAlign: 'center', fontWeight: 600 }}>{day.format('ddd, MMM D, YYYY')}</span>
          <Button
            size='small'
            aria-label='Next day'
            icon={<CaretRightOutlined />}
            disabled={isTodayOrLater(day)}
            onClick={() => setDay(day.clone().add(1, 'day').startOf('day'))}
          />
        </Row>
      )}
      {hasAnyData ? (
        view === 'data' ? (
          <ChartDataGrid
            rows={gridRows}
            valueColumns={gridColumns}
            // Daily buckets aggregate a whole day → date-only (no single
            // reading time). The 15-min "Today" buckets are real intra-day
            // windows, so show their date+time of day.
            dateOnly={isDaily}
            csvName='tracpatch-leg-position'
          />
        ) : (
          <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            <div ref={tooltipRef} className='chartToolTip' style={{ position: 'absolute', visibility: 'hidden', zIndex: 10 }} />
            <svg style={{ width: '100%', height: '360px' }} ref={svgRef} />
          </div>
        )
      ) : (
        <Row justify='center' align='middle' style={{ height: 200 }}>
          <h2>Data not available</h2>
        </Row>
      )}
    </>
  )
}

const mapStateToProps = (state) => ({ patient: state.patient.patient })
const mapDispatchToProps = () => ({})
export default connect(mapStateToProps, mapDispatchToProps)(TracPatchLegPositionChart)
